import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Minus, Leaf, Sparkles, Heart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { HoverVideoPreview } from "@/components/HoverVideoPreview";
import { inr, imageUrl } from "@/lib/api";
import { PRODUCT } from "@/constants/testIds";

/**
 * @param product { id, name, price, mrp, images, is_green_cracker, is_bestseller, is_new, category_id }
 * @param kind    'product' | 'combo'
 */
export function ProductCard({ product, kind = "product" }) {
  const { getItem, addItem, setQty } = useCart();
  const { has, toggle } = useWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();
  const inCart = getItem(kind, product.id);
  const qty = inCart?.qty || 0;
  const discount = product.mrp && product.mrp > product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;
  const primaryIdx = product.primary_image_index || 0;
  const img = imageUrl(product.images?.[primaryIdx] || product.images?.[0] || product.image_url || "");
  const detailHref = kind === "product"
    ? `/products/${product.slug || product.id}`
    : `/combos/${product.slug || product.id}`;
  const wished = kind === "product" && has(product.id);

  const handleWish = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.info("Please sign in to save items to your wishlist");
      navigate("/login");
      return;
    }
    const res = await toggle(product.id);
    if (res?.in_wishlist === true) toast.success("Added to wishlist");
    else if (res?.in_wishlist === false) toast("Removed from wishlist");
  };

  const handleAdd = () => {
    addItem({
      kind,
      ref_id: product.id,
      name: product.name,
      unit_price: product.price,
      mrp: product.mrp,
      image_url: img,
      category_id: product.category_id,
      is_green_cracker: !!product.is_green_cracker,
    });
  };

  return (
    <div
      data-testid={PRODUCT.card(product.id)}
      className="relative bg-hyd-panel rounded-2xl border border-hyd-border overflow-hidden flex flex-col hover:border-hyd-gold/60 transition-colors shadow-card-dark"
    >
      <Link to={detailHref} className="relative aspect-square bg-hyd-panel2 block">
        {product.video_url ? (
          <HoverVideoPreview videoUrl={product.video_url} poster={img} alt={product.name} roundedCls="rounded-none" />
        ) : img ? (
          <img src={img} alt={product.name} loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center text-hyd-text3"><Sparkles className="w-10 h-10" /></div>
        )}
        {/* Tags */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {product.is_bestseller && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-hyd-red text-white uppercase tracking-wider">Best</span>
          )}
          {product.is_new && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-hyd-gold text-hyd-bg uppercase tracking-wider">New</span>
          )}
        </div>
        {product.is_green_cracker && (
          <div className="absolute top-2 right-2 flex items-center gap-1 bg-emerald-600/90 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider">
            <Leaf className="w-3 h-3" /> Green
          </div>
        )}
        {kind === "product" && (
          <button
            data-testid={PRODUCT.wishBtn(product.id)}
            aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
            onClick={handleWish}
            className={`absolute ${product.is_green_cracker ? "top-8" : "top-2"} right-2 w-7 h-7 grid place-items-center rounded-full bg-hyd-bg/70 backdrop-blur border border-hyd-border transition-colors hover:border-hyd-red`}
          >
            <Heart className={`w-3.5 h-3.5 ${wished ? "fill-hyd-red text-hyd-red" : "text-hyd-text2"}`} />
          </button>
        )}
        {discount > 0 && (
          <div className="absolute bottom-2 left-2 bg-hyd-bg/85 text-hyd-gold text-[11px] font-bold px-2 py-0.5 rounded-full">
            {discount}% OFF
          </div>
        )}

        {/* Add / Stepper — overlaps bottom of image */}
        <div className="absolute -bottom-4 right-3 z-10">
          {qty === 0 ? (
            <button
              data-testid={PRODUCT.addBtn(product.id)}
              onClick={(e) => { e.preventDefault(); handleAdd(); }}
              aria-label={`Add ${product.name} to cart`}
              className="bg-hyd-red text-white font-bold uppercase tracking-wider text-xs px-5 py-2 rounded-full shadow-glow-red hover:bg-hyd-redDim transition-colors animate-pop"
            >
              + Add
            </button>
          ) : (
            <div className="bg-hyd-bg border border-hyd-red text-hyd-red flex items-center rounded-full px-1 py-1 gap-1 shadow-glow-red animate-pop">
              <button
                data-testid={PRODUCT.decBtn(product.id)}
                aria-label="Decrease quantity"
                onClick={(e) => { e.preventDefault(); setQty(kind, product.id, qty - 1); }}
                className="w-7 h-7 grid place-items-center hover:text-white transition-colors rounded-full"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span data-testid={PRODUCT.qty(product.id)} className="min-w-[20px] text-center text-sm font-bold text-white">
                {qty}
              </span>
              <button
                data-testid={PRODUCT.incBtn(product.id)}
                aria-label="Increase quantity"
                onClick={(e) => { e.preventDefault(); setQty(kind, product.id, qty + 1); }}
                className="w-7 h-7 grid place-items-center hover:text-white transition-colors rounded-full"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </Link>

      <Link to={detailHref} className="p-3 pt-5 flex flex-col gap-1.5 block">
        <div data-testid={PRODUCT.name(product.id)} className="text-sm font-semibold text-hyd-text line-clamp-2 min-h-[2.5rem]">
          {product.name}
        </div>
        {product.short_description && (
          <div className="text-[11px] text-hyd-text3 line-clamp-1">{product.short_description}</div>
        )}
        <div className="flex items-baseline gap-2">
          <div data-testid={PRODUCT.price(product.id)} className="text-hyd-gold font-bold text-base">
            {inr(product.price)}
          </div>
          {product.mrp && product.mrp > product.price && (
            <div className="text-hyd-text3 text-xs line-through">{inr(product.mrp)}</div>
          )}
        </div>
      </Link>
    </div>
  );
}
